import React, { useState, useMemo } from 'react';
import { IoChevronBack, IoChevronForward, IoSchoolOutline, IoCard, IoStar } from 'react-icons/io5';
import { Deadline, DeadlineType } from '../index';

interface CalendarViewProps {
  deadlines: Deadline[];
  onEditDeadline: (deadline: Deadline) => void;
  getTypeIcon: (type: DeadlineType) => JSX.Element;
  getTypeColor: (type: DeadlineType) => string;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  deadlines,
  onEditDeadline,
  getTypeIcon,
  getTypeColor,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  
  // Get calendar data for current month
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Get first day of month and how many days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Calculate previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    // Create calendar grid
    const calendarDays = [];
    
    // Add previous month's trailing days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      calendarDays.push({
        date: daysInPrevMonth - i,
        isCurrentMonth: false,
        fullDate: new Date(year, month - 1, daysInPrevMonth - i),
      });
    }
    
    // Add current month's days
    for (let date = 1; date <= daysInMonth; date++) {
      calendarDays.push({
        date,
        isCurrentMonth: true,
        fullDate: new Date(year, month, date),
      });
    }
    
    // Add next month's leading days to complete the grid
    const remainingCells = 42 - calendarDays.length; // 6 rows × 7 days
    for (let date = 1; date <= remainingCells; date++) {
      calendarDays.push({
        date,
        isCurrentMonth: false,
        fullDate: new Date(year, month + 1, date),
      });
    }
    
    return calendarDays;
  }, [currentDate]);

  // Group deadlines by date
  const deadlinesByDate = useMemo(() => {
    const grouped: Record<string, Deadline[]> = {};
    deadlines.forEach(deadline => {
      const dateKey = new Date(deadline.dueDate).toDateString();
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(deadline);
    });
    return grouped;
  }, [deadlines]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Summary stats for current month
  const monthlyStats = useMemo(() => {
    const currentMonthDeadlines = deadlines.filter(deadline => {
      const deadlineDate = new Date(deadline.dueDate);
      return deadlineDate.getMonth() === currentDate.getMonth() &&
             deadlineDate.getFullYear() === currentDate.getFullYear();
    });

    return {
      applications: currentMonthDeadlines.filter(d => d.type === 'application').length,
      scholarships: currentMonthDeadlines.filter(d => d.type === 'scholarship').length,
      opportunities: currentMonthDeadlines.filter(d => d.type === 'opportunity').length,
    };
  }, [deadlines, currentDate]);

  return (
    <div className="calendar-view">
      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="calendar-nav">
          <button className="nav-btn" onClick={() => navigateMonth('prev')}>
            <IoChevronBack />
          </button>
          <h2 className="calendar-title">{monthName}</h2>
          <button className="nav-btn" onClick={() => navigateMonth('next')}>
            <IoChevronForward />
          </button>
        </div>
        <button className="today-btn" onClick={goToToday}>
          Today
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-container">
        <div className="calendar-grid">
          {/* Day headers */}
          <div className="calendar-day-headers">
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
              <div key={day} className="day-header">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar cells */}
          <div className="calendar-cells">
            {calendarData.map((day, index) => {
              const dayDeadlines = deadlinesByDate[day.fullDate.toDateString()] || [];
              const isCurrentMonthDay = day.isCurrentMonth;
              const isTodayDay = isToday(day.fullDate);
              
              return (
                <div 
                  key={index}
                  className={`calendar-cell ${!isCurrentMonthDay ? 'other-month' : ''} ${isTodayDay ? 'today' : ''}`}
                >
                  <div className="cell-date">{day.date}</div>
                  {isTodayDay && <div className="today-indicator">Today</div>}
                  
                  <div className="cell-events">
                    {dayDeadlines.slice(0, 2).map(deadline => ( // Show max 2 events per day
                      <div
                        key={deadline.id}
                        className={`calendar-event calendar-event-${getTypeColor(deadline.type)}`}
                        onClick={() => onEditDeadline(deadline)}
                        title={`${deadline.name} - ${deadline.description}`}
                      >
                        <div className="event-name">{deadline.name}</div>
                        <div className="event-description">
                          {deadline.type === 'application' ? 'Application' :
                           deadline.type === 'scholarship' ? 'Scholarship' : 'Opportunity'}
                        </div>
                      </div>
                    ))}
                    {dayDeadlines.length > 2 && (
                      <div className="more-events">
                        +{dayDeadlines.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="calendar-summary">
        <div className="summary-card">
          <div className="summary-icon summary-icon-blue">
            <IoSchoolOutline />
          </div>
          <div className="summary-content">
            <span className="summary-number">{monthlyStats.applications}</span>
            <h3 className="summary-title">Application Deadlines</h3>
            <p className="summary-subtitle">Due this month</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon summary-icon-green">
            <IoCard />
          </div>
          <div className="summary-content">
            <span className="summary-number">{monthlyStats.scholarships}</span>
            <h3 className="summary-title">Scholarship Deadlines</h3>
            <p className="summary-subtitle">Due this month</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon summary-icon-purple">
            <IoStar />
          </div>
          <div className="summary-content">
            <span className="summary-number">{monthlyStats.opportunities}</span>
            <h3 className="summary-title">Important Opportunities</h3>
            <p className="summary-subtitle">Due this month</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;